import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AppBar, Button, Box, Menu, MenuItem } from "@mui/material";

import {
  brainVideoData,
  spineVideoData,
  shoulderVideoData,
  kneeVideoData,
  ankleNfootVideoData,
  elbowNwristVideoData,
  hipVideoData,
  jawVideoData,
  phaseOfCareVideoData,
  treatmentsVideoData,
  nervousSystemVideoData,
} from "../../utils/videoData";

import { getColors } from "../../themes/theme";

import { ModeChangebar } from "./ModeChangebar";
import { ScrollableToolbar } from "./ScrollableToolbar";
import SmartDisplayIcon from "@mui/icons-material/SmartDisplay";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropDown";
import CheckIcon from "@mui/icons-material/Check";
import useMediaQuery from "@mui/material/useMediaQuery";

export const VideoTopbar = () => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const navigate = useNavigate();
  const { section, videoId, selectedSubItem } = useParams();
  const [activeSubItem, setActiveSubItem] = useState(selectedSubItem || "");
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const videoDataMap = {
    brain: brainVideoData,
    spine: spineVideoData,
    shoulder: shoulderVideoData,
    knee: kneeVideoData,
    ankleNfoot: ankleNfootVideoData,
    elbowNwrist: elbowNwristVideoData,
    hip: hipVideoData,
    jaw: jawVideoData,
    nervous_system: nervousSystemVideoData,
    treatments: treatmentsVideoData,
    phase_of_care: phaseOfCareVideoData,
  };

  const videoDetails = videoDataMap[section]?.find(
    (video) => video.routing === `/video/${videoId}`
  );

  useEffect(() => {
    if (!selectedSubItem && Object.keys(videoDetails.subItems).length > 0) {
      const firstSubItem = Object.keys(videoDetails.subItems)[0];
      setActiveSubItem(firstSubItem);
      navigate(`/${section}/${videoId}/${firstSubItem}`, { replace: true });
    } else {
      setActiveSubItem(selectedSubItem || "");
    }
  }, [selectedSubItem, videoDetails, section, videoId, navigate]);

  const handleSubItemClick = (subItem) => {
    setActiveSubItem(subItem);
    if (
      section === "knee" &&
      videoId === "knee" &&
      subItem === "Interactive Knee Degeneration"
    )
      window.open("/webgl/interactive-knee-degeneration/index.html", "_blank");
    else if (
      section === "shoulder" &&
      videoId === "shoulder" &&
      subItem === "Interactive Rotator Cuff Injury"
    )
      window.open(
        "/webgl/interactive-rotator-cuff-injury/index.html",
        "_blank"
      );
    else if (
      section === "spine" &&
      videoId === "whiplash" &&
      subItem === "Interactive Whiplash"
    )
      window.open("/webgl/interactive-whiplash/index.html", "_blank");
    else navigate(`/${section}/${videoId}/${subItem}`);
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelection = (section) => {
    handleClose();
    handleSubItemClick(section);
  };

  return (
    <>
      <AppBar
        position="static"
        sx={{
          boxShadow: 0,
          py: { xs: 2, md: 4 },
          px: { xs: 1, md: 2 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            flexDirection: "row",
            alignItems: "center",
            gap: 2,
            px: { xs: 2, sm: 3 },
          }}
        >
          {section !== "nervous_system" &&
            section !== "jaw" &&
            section !== "hip" && (
              <>
                <ScrollableToolbar>
                  {videoDetails?.subItems &&
                    Object.keys(videoDetails.subItems).length > 1 &&
                    Object.entries(videoDetails.subItems).map(
                      ([key, value], index) => (
                        <Button
                          key={index}
                          variant="outlined"
                          onClick={() => handleSubItemClick(key)}
                          sx={{
                            fontSize: "20px",
                            px: 3,
                            py: "14px",
                            height: "60px",
                            mr: "20px",
                            borderRadius: "100px",
                            color:
                              activeSubItem === key && getColors.buttonText,
                            border:
                              activeSubItem === key && "2px solid transparent",
                            background:
                              activeSubItem === key
                                ? getColors.buttonBg
                                : "transparent",
                            width: "max-content",
                            minWidth: "max-content",
                          }}
                        >
                          {key}
                        </Button>
                      )
                    )}
                </ScrollableToolbar>
                <Box sx={{ display: { xs: "block", sm: "none" } }}>
                  <Button
                    startIcon={<SmartDisplayIcon />}
                    endIcon={open ? <ArrowDropDownIcon /> : <ArrowDropUpIcon />}
                    onClick={handleClick}
                    variant="contained"
                  >
                    {"Videos"}
                  </Button>
                  <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                    {videoDetails?.subItems &&
                      Object.keys(videoDetails.subItems).length > 1 &&
                      Object.entries(videoDetails.subItems).map(
                        ([key, value], index) => (
                          <MenuItem
                            onClick={() => handleSelection(key)}
                            key={index}
                          >
                            {selectedSubItem === key ? (
                              <CheckIcon style={{ marginRight: 8 }} />
                            ) : (
                              <Box style={{ width: 32 }} />
                            )}
                            {key}
                          </MenuItem>
                        )
                      )}
                  </Menu>
                </Box>
              </>
            )}
          {section === "nervous_system" && isMobile && (
            <Box>
              <Button
                startIcon={<SmartDisplayIcon />}
                endIcon={open ? <ArrowDropDownIcon /> : <ArrowDropUpIcon />}
                onClick={handleClick}
                variant="contained"
              >
                {"Videos"}
              </Button>
              <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                {videoDetails?.subItems &&
                  Object.keys(videoDetails.subItems).length > 1 &&
                  Object.entries(videoDetails.subItems).map(
                    ([key, value], index) => (
                      <MenuItem
                        onClick={() => handleSelection(key)}
                        key={index}
                      >
                        {selectedSubItem === key ? (
                          <CheckIcon style={{ marginRight: 8 }} />
                        ) : (
                          <Box style={{ width: 32 }} />
                        )}
                        {key}
                      </MenuItem>
                    )
                  )}
              </Menu>
            </Box>
          )}
          <ModeChangebar isDisable={true} />
        </Box>
      </AppBar>
    </>
  );
};
